/**
 * Analytics utility for tracking user interactions with Google Analytics 4 (GA4)
 */

declare global {
    interface Window {
        gtag: (...args: any[]) => void;
    }
}

/**
 * Tracks a click on a WhatsApp contact button
 * @param location Description of where the button is (e.g., 'Floating Bubble', 'Hero Section')
 */
export const trackWhatsAppClick = (location: string) => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'contact_whatsapp', {
            'button_location': location,
            'event_category': 'Conversion',
            'event_label': location,
            'transport_type': 'beacon'
        });

        if (import.meta.env.DEV) {
            console.log(`[Analytics] Tracked WhatsApp click from: ${location}`);
        }
    }
};

/**
 * Sends lead data via Web3Forms
 */
export async function captureLead(data: {
    email: string;
    name?: string;
    phone?: string;
    source: string;
    message?: string;
}) {
    try {
        let subjectText = `New Lead Captured: ${data.source}`;
        if (data.source.includes('Wishlist Modal')) {
            subjectText = `🌟 New Wishlist Lead: ${data.name || "A User"}`;
        }

        const web3formsPayload = {
            access_key: "ee06da99-1582-4a82-80e6-e7196b072777",
            subject: subjectText,
            from_name: data.name || "Unknown",
            email: data.email,
            phone: data.phone || "Not provided",
            source: data.source,
            message: data.message || "A user submitted their details through a lead capture form or WhatsApp popup."
        };

        const response = await fetch('https://api.web3forms.com/submit', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify(web3formsPayload),
        });

        if (!response.ok) throw new Error('Failed to capture lead');
        console.log(`%c[Lead Captured via Web3Forms] ${data.source}: ${data.email}`, 'color: #25D366; font-weight: bold;');
    } catch (err) {
        console.error('Lead capture error:', err);
    }
}

/**
 * Tracks when a user starts filling out a form
 */
export function trackFormStart(formName: string) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'form_start', {
            'form_name': formName,
        });
        if (import.meta.env.DEV) {
            console.log(`[Analytics] Form Start: ${formName}`);
        }
    }
}

/**
 * Tracks scroll depth
 */
export function trackScrollDepth(percentage: number, label: string) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'scroll_depth', {
            'percentage': percentage,
            'page_location': label,
        });
        if (import.meta.env.DEV) {
            console.log(`[Analytics] Scroll Depth: ${percentage}% on ${label}`);
        }
    }
}
